/* eslint-disable max-lines -- Deprecated code: will be removed once objects are released */
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
  Injector,
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { isEqual, isNil, mapValues, size } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { map as _map } from 'lodash';
import { combineLatest, merge, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  skip,
  switchMap,
  switchMapTo,
} from 'rxjs/operators';
import { Selection } from '../../../../../../core/h3ml/state/fusion-state';
import { H3mlState } from '../../../../../../core/h3ml/state/h3ml-state';
import { H3mlStore } from '../../../../../../core/h3ml/state/h3ml-store';
import {
  OutputInfo,
  OutputInfoByName,
  outputInfoStoreToken,
} from '../../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { isTabName } from '../../../../../../core/outputs/annotation/annotation-shared/state/layers/transient-shape-layer-state';
import { UndoService } from '../../../../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/undo.service';
import { ObjectlessFocusedShapeService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { ShapeSelectionService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { ShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { keys } from '../../../../../../cross-team-shared/misc-utils/keys';
import { isPointCloudOutput } from '../../../../../../cross-team-shared/misc-utils/output-utils';
import { isOrthographicShape3D } from '../../../../../../cross-team-shared/state/shape.state';
import { EventTrackingService } from '../../../../../../shared/event-tracking/event-tracking.service';
import {
  debounceOnMicroQueue,
  delayOnMicrotaskQueue,
} from '../../../../../../shared/misc-utilities/misc-utils';
import { observeKeys } from '../../../../../../shared/misc-utilities/store-utils';
import { AnnotationOutput } from '../../../../../../team-apis/otterdam/outputs/annotation-output';
import { AbstractOutput } from '../../../../../../team-apis/otterdam/outputs/output';
import { outputAnnotationInfoStoreToken } from '../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../state/root/abstract-annotation-store';
import { TagService } from '../../../utils/tag.service';

// https://github.com/ng-packagr/ng-packagr/issues/696#issuecomment-373487183
// @dynamic
@Component({
  selector: 'h3-objectless-tag-editor',
  templateUrl: './objectless-tag-editor.component.html',
  styleUrls: ['./objectless-tag-editor.component.scss'],
  providers: [
    {
      provide: H3mlStore,
      useFactory: () => new H3mlStore(new H3mlState()),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/** @deprecated used for old projects without objects */
export class ObjectlessTagEditorComponent extends DirectiveSuperclass {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );

  faArrowLeft = faArrowLeft;
  needsTabs$: Observable<boolean>;
  focusedShapeStore$?: Observable<Store<ShapeState> | undefined>;
  frameNumber$: Observable<number>;
  maxFrameNumber$ = this.workspaceStore.getMaxFrameNumber$(
    this.outputInfoStore.state().output.name,
  );
  selectedTabIndex$: Observable<number>;

  private updatingTags = false;

  constructor(
    private annotationStore: AbstractAnnotationStore,
    private editorStore: H3mlStore,
    private eventTrackingService: EventTrackingService,
    private fusionManager: FusionManager,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private tagService: TagService,
    private undoService: UndoService,
    private workspaceStore: TaskWorkspaceStore,
    private selectionService: ShapeSelectionService,
    private focusedShapeService: ObjectlessFocusedShapeService,
    injector: Injector,
  ) {
    super(injector);
    this.initializeEditorStore();
    this.trackFocusedShape();
    this.subscribeToSelection();
    this.frameNumber$ = annotationStore('frameNumber').$;
    this.focusedShapeStore$ = focusedShapeService.getFocusedShapeStore$();

    this.needsTabs$ = this.buildNeedsTabs$();
    this.selectedTabIndex$ = this.getActiveLayerStore()('transient')(
      'tagEditorActiveTab',
    ).$.pipe(map((activeTab) => (activeTab === 'Static' ? 0 : 1)));
  }

  closeTagEditor(source = 'back_button', notifyFusionManager = true) {
    this.tagService.closeTagEditor(source, undefined, notifyFusionManager);
  }

  tabSelected(tabChangeEvent: MatTabChangeEvent) {
    const shapeStore = this.focusedShapeService.getFocusedShapeStore();
    if (!shapeStore) {
      return;
    }

    const selectedTabName = tabChangeEvent.tab.textLabel;
    assert(
      isTabName(selectedTabName),
      'Selected tab should always be a TabName',
    );

    this.tagService.switchTagEditorTab(
      selectedTabName,
      shapeStore.state().index,
    );
    this.eventTrackingService.track({
      id: 'select_tag_editor_tab',
      bucket: 'tag_selection',
      name: selectedTabName.toLowerCase(),
    });
  }

  private initializeEditorStore() {
    const taskWorkspaceState = this.workspaceStore.state();
    const outputInfoByName: OutputInfoByName = {};
    for (const option of this.outputInfoStore.state().output.answer_options) {
      outputInfoByName[option.name] = new OutputInfo(
        taskWorkspaceState.outputInfoByName[option.name].output,
      );
    }
    this.editorStore.assign({
      project: taskWorkspaceState.project,
      task: taskWorkspaceState.task,
      outputInfoByName,
    });
  }

  private buildNeedsTabs$() {
    const answerOptionStore = this.outputInfoStore('output')('answer_options');
    const staticAnswerOptions$ = answerOptionStore.$.pipe(
      map((answerOptions) => answerOptions.filter((option) => !option.dynamic)),
    );
    const dynamicAnswerOptions$ = answerOptionStore.$.pipe(
      map((answerOptions) => answerOptions.filter((option) => option.dynamic)),
    );

    return combineLatest([
      staticAnswerOptions$,
      dynamicAnswerOptions$,
      this.fusionManager.areAllSingleFrame$,
    ]).pipe(
      map(
        ([staticOptions, dynamicOptions, singleFrame]) =>
          !singleFrame && !!staticOptions.length && !!dynamicOptions.length,
      ),
    );
  }

  private trackFocusedShape() {
    const updateTagEditor$ = this.focusedShapeService
      .getFocusedShapeStore$()
      .pipe(
        switchMap((store) =>
          store ? observeKeys(store, 'tags', 'dynamic_tags').pipe() : of(1),
        ),
        filter(() => !this.updatingTags),
        switchMapTo(this.annotationStore('frameNumber').$),
        // Debounce to dedupe when the focused shape changes twice with a single
        // user action (e.g. when creating a point, it first changes to
        // `undefined` when the point is placed in the overlay, then during the
        // same mousedown changes again to be the new point when it is added to
        // the main layer).
        debounceOnMicroQueue(),
      );
    this.subscribeTo(updateTagEditor$, this.copyFromShapeToTagEditor);

    const updateShapeStore$ = updateTagEditor$.pipe(
      // Use new observables every time we copy into the tag editor (above),
      // so that we can skip the first render after that. Otherwise we'll get
      // key frames & undo states just from displaying the tag editor.
      switchMap(() => merge(...this.getObservablesForNestedAnswerChanges())),
      // Because we will change the store in response to a change in the
      // store, delay to avoid https://github.com/ngrx/platform/issues/2322
      delayOnMicrotaskQueue(),
    );
    this.subscribeTo(updateShapeStore$, (response) => {
      this.copyFromTagEditorToShape(...response);
    });
  }

  private subscribeToSelection() {
    this.subscribeTo(
      // @ts-ignore -- fix me
      this.selectionService.getSelectionForOutput$(true).pipe(skip(1)),
      (selection) => {
        if (
          size(selection) !== 1 ||
          this.isFocusedOrthographicShape(selection)
        ) {
          // Note: This will not trigger an event for the undo/redo case, since
          // the undo/redo logic will run first.
          // Selection is synched up with fusion, no need to sync this as well
          this.closeTagEditor('change_shape_selection', false);
        }
      },
    );
  }

  private copyFromShapeToTagEditor(frameNumber: number) {
    const shapeStore = this.focusedShapeService.getFocusedShapeStore();
    const outputInfoByName = mapValues(
      this.editorStore.state().outputInfoByName,
      (info, outputName) => {
        let answer;
        if (shapeStore) {
          answer = this.tagService.getCurrentTag(
            shapeStore,
            outputName,
            frameNumber,
          );
        }
        return {
          ...info,
          answer,
          initialized: false,
          isVisible: shapeStore ? this.isOutputVisible(info.output) : true,
        };
      },
    );
    const readOnly = this.workspaceStore.state().readOnly || !shapeStore;
    this.editorStore.assign({
      outputInfoByName,
      readOnly,
    });
  }

  private isOutputVisible(output: AbstractOutput) {
    if (isNil(output.depends_on_answers_from_output_id)) {
      return true;
    }
    const parentOutput = this.tagService.getNestedOutput(
      output.depends_on_answers_from_output_id,
    )!;
    return this.tagService.isParentOptionSelected(parentOutput, output);
  }

  private getObservablesForNestedAnswerChanges() {
    // wait until the output is initialized, then pairwise will skip one
    // because we only want to react to changes AFTER initialization
    // (that the user makes)
    return _map(
      this.outputInfoStore.state().output.answer_options,
      (answerOption) =>
        this.editorStore('outputInfoByName')(answerOption.name).$.pipe(
          filter((info) => info.initialized),
          delayOnMicrotaskQueue(),
          distinctUntilChanged(
            (prev, cur) =>
              isEqual(cur.answer, prev.answer) &&
              cur.isVisible === prev.isVisible,
          ),
          pairwise(),
        ),
    );
  }

  private copyFromTagEditorToShape(previousInfo: OutputInfo, info: OutputInfo) {
    const shapeIndex = this.focusedShapeService
      .getFocusedShapeStore()
      ?.state().index;
    if (!shapeIndex) {
      return;
    }

    const frameNumber = this.annotationStore.state().frameNumber;
    this.updatingTags = true;
    this.tagService.updateTag(
      info,
      shapeIndex,
      frameNumber,
      previousInfo.isVisible,
    );
    this.updatingTags = false;
    this.undoService.pushCurrentState({
      collectKey: info.output.name,
      collectDebounce: 1000,
    });
  }

  private getActiveLayerStore() {
    // This component only exists if the active layer is a shape layer
    return this.annotationStore.getActiveShapeLayerStore()!;
  }

  private isFocusedOrthographicShape(selection: Selection) {
    const shape = this.shapesStore(keys(selection)[0]).state();
    return (
      // fix me if you change this code
      shape &&
      isPointCloudOutput(this.outputInfoStore.state().output) &&
      isOrthographicShape3D(shape)
    );
  }
}
